import { Component, Inject, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { User } from '@precisely/prism-ng/user-account';
import { MenuItem } from 'primeng/api';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppWindow, WINDOW } from '../../AppWindow';
import { AuthorizationService } from '../../authorization/authorization.service';
import { FeatureFlagService } from '../../core/feature-flags/feature-flag.service';
import { LD_FLAGS, LaunchDarklyFlagService } from '../../core/feature-flags/launch-darkly-flag.service';
import { EnterpriseConfigurationDataAccessService } from '../../shared/modules/enterprise-configuration/enterprise-configuration-data-access.service';
import { AbstractHeader } from '../abstract-header';

@Component({
  selector: 'app-authenticated-header',
  templateUrl: './authenticated-header.component.html',
  styleUrls: ['./authenticated-header.component.scss'],
})
export class AuthenticatedHeaderComponent extends AbstractHeader implements OnInit {
  public user$: Observable<User | undefined>;
  public isSigningOut = false;
  public changePasswordUrl?: string;
  public isNewOktaURLEnabled = false;
  public readonly bodyItemsDefault: MenuItem[] = [{ label: 'Manage Users', url: '/uam' }];
  public readonly bodyItemsModern: MenuItem[] = [{ label: 'API Keys', url: '/api-keys' }];
  public copyrightItems: MenuItem[] = [
    { label: 'Legal', url: 'https://www.precisely.com/legal' },
    { label: 'Contact Sales', url: 'https://www.precisely.com/contact' },
  ];
  public trialUrl = '';
  public isPdxModernizationEnabled$ = this._ldFlagService.getFlag$(LD_FLAGS.PDX_PORTAL_MODERNIZATION);

  constructor(
    private _authorization: AuthorizationService,
    _enterpriseConfigurationService: EnterpriseConfigurationDataAccessService,
    _sanitizer: DomSanitizer,
    @Inject(WINDOW) private _window: AppWindow,
    private _featureFlagService: FeatureFlagService,
    private _ldFlagService: LaunchDarklyFlagService,
  ) {
    super(_enterpriseConfigurationService, _sanitizer);
    this.trialUrl = this.getTrialUrl();
    this.user$ = this._authorization.principal$.pipe(
      map((principal) => {
        if (principal?.identity?.isAuthenticated) {
          return {
            firstName: principal.identity.givenName!,
            lastName: principal.identity.familyName!,
            email: principal.identity.email!,
          };
        }
        return undefined;
      }),
    );

    this.changePasswordUrl = this._window.portalConfig.masterCardResetPasswordUrl;
  }

  ngOnInit() {
    void this._setIsNewOktaURLEnabled();
  }

  logout() {
    this._authorization.logout();
  }

  public onClick() {
    window.open(this.trialUrl, '_blank');
  }

  private async _setIsNewOktaURLEnabled() {
    const features = await firstValueFrom(this._featureFlagService.getFeatures());
    if (features.mastercard_new_okta_url?.isEnabled) {
      this.isNewOktaURLEnabled = true;
    }
  }

  private getTrialUrl(): string {
    const host = window.location.hostname;

    if (host.includes('portal-dev.data.precisely.services')) {
      return 'https://developer-dev.cloud.precisely.com/';
    } else if (host.includes('data.precisely.com')) {
      return 'https://developer.cloud.precisely.com/';
    } else if (host.includes('data.precisely.services')) {
      return 'https://developer-stg.cloud.precisely.com/';
    } else {
      return 'https://developer-dev.cloud.precisely.com/';
    }
  }
}
